"use client"

import { supabase } from "@/lib/supabase/browser-client"
import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import { ChatbotUIContext } from "@/context/context"
import { PermOptions, permOptionsKey } from '@/types/base/permissions'
import { authCanPerform } from '@/types/base/authuser'
import { logError } from "@/utils/errorLogging"
import { createClientLogger } from "@/utils/logger/clientLogger"

const logger = createClientLogger('context.permissionContext', supabase)

export type Cache<T> = Record<string, T>

interface PermissionContextProps {
  checkPermission: (options: PermOptions) => void
  permissions: Cache<boolean>
  loadingPermissions: Cache<boolean>
	clearPermissionCache: () => void
  invalidatePermissionCacheEntry: (key: string) => void
	hydratePermissions: (perms: Cache<boolean>) => void
}

const PermissionContext = createContext<PermissionContextProps | undefined>(undefined)

const PermissionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
	const { authuser } = useContext(ChatbotUIContext)

  const [permissions, setPermissions] = useState<Cache<boolean>>({});
  const [loadingPermissions, setLoadingPermissions] = useState<Cache<boolean>>({});

  const canPerform = async (options: PermOptions): Promise<boolean> => {
		try {
			if (authuser) {
				return await authCanPerform(supabase, authuser, options)
			}
			return false
		}
		catch (error) {
      logError(logger, error, 'Error checking permission:')
			return false
		}
  }

  const checkPermission = useCallback(async (options: PermOptions) => {
		const key = permOptionsKey(options)
    if (permissions[key] !== undefined || loadingPermissions[key]) return

    setLoadingPermissions((prev) => ({ ...prev, [key]: true }))
    const result = await canPerform(options)
    setPermissions((prev) => ({ ...prev, [key]: result }))
    setLoadingPermissions((prev) => ({ ...prev, [key]: false }))
  }, [permissions, loadingPermissions, setLoadingPermissions, setPermissions, authuser])

	const clearPermissionCache = useCallback(() => {
    setPermissions({})
    setLoadingPermissions({})
  }, [])

  const invalidatePermissionCacheEntry = useCallback((key: string) => {
		logger.info('Invalidating key:');
    setPermissions(prev => {
      const { [key]: _, ...rest } = prev
      return rest
    })
    setLoadingPermissions(prev => {
      const { [key]: _, ...rest } = prev
      return rest
    })
  }, [])

	const hydratePermissions = useCallback((perms: Cache<boolean>) => {
		setPermissions(prev => ({ ...prev, ...perms }))
	}, [])

  return (
    <PermissionContext.Provider value={{ permissions, loadingPermissions, checkPermission,
				clearPermissionCache, invalidatePermissionCacheEntry,  hydratePermissions }}>
      {children}
    </PermissionContext.Provider>
  )
}

const usePermission = () => {
  const context = useContext(PermissionContext)
  if (!context) {
    throw new Error('usePermission must be used within a PermissionProvider')
  }
  return context
}

export function buildPermissionCache<T extends { permissions: Record<string, boolean> }>(
  items: T[]
): Record<string, boolean> {
  const cache: Record<string, boolean> = {}
  for (const item of items) {
    Object.assign(cache, item.permissions)
  }
  return cache
}

export { PermissionContext, PermissionProvider, usePermission }